import { Shield } from "lucide-react";
import { Plus } from "lucide-react";
import { WifiOff } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { isHorizontalScrollZone } from "@/lib/swipe";
import {
  LayoutDashboard, LibraryBig, FolderOpen, CalendarDays, ClipboardCheck,
  Package, Heart, ListMusic, Users, Settings, Bell, Music2, Check, Home, ChevronUp,
  LogOut, GraduationCap, School, ShoppingBasket, LifeBuoy,
} from "lucide-react";
import api, { logoUrl } from "@/lib/api";
import { dateLocale } from "@/lib/i18n";
import { ensurePushSubscription } from "@/lib/push";
import TutorialCarousel from "@/components/TutorialCarousel";
import GuidedTour from "@/components/GuidedTour";
import NotifSetupDialog from "@/components/NotifSetupDialog";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import AvailabilityButtons from "@/components/AvailabilityButtons";
import { FeeRib, claimFeePaid } from "@/components/MyFeesCard";
import { eurCents } from "@/components/FeesPanel";
import { HandCoins } from "lucide-react";
import { toast } from "sonner";

const NAV_MAIN = [
  { to: "/tableau-de-bord", label: "nav.dashboard", icon: LayoutDashboard, tid: "nav-dashboard-link" },
  { to: "/bibliotheque", label: "nav.library", icon: LibraryBig, tid: "nav-library-link" },
  { to: "/documents", label: "nav.documents", icon: FolderOpen, tid: "nav-documents-link" },
  { to: "/agenda", label: "nav.agenda", icon: CalendarDays, tid: "nav-agenda-link" },
  { to: "/achats", label: "nav.purchases", icon: ShoppingBasket, tid: "nav-purchases-link" },
  { to: "/presence", label: "nav.presence", icon: ClipboardCheck, tid: "nav-presence-link" },
  { to: "/inventaire", label: "nav.inventory", icon: Package, tid: "nav-inventory-link" },
  { to: "/favoris", label: "nav.favorites", icon: Heart, tid: "nav-favorites-link" },
  { to: "/setlists", label: "nav.setlists", icon: ListMusic, tid: "nav-setlists-link" },
];
const NAV_BOTTOM = [
  { to: "/utilisateurs", label: "nav.users", icon: Users, tid: "nav-users-link" },
  { to: "/parametres", label: "nav.settings", icon: Settings, tid: "nav-settings-link" },
  { to: "/aide", label: "nav.help", icon: LifeBuoy, tid: "nav-help-link" },
];
const NAV_MOBILE = [
  { to: "/tableau-de-bord", label: "nav.home", icon: Home, tid: "mnav-home-link" },
  { to: "/bibliotheque", label: "nav.library", icon: LibraryBig, tid: "mnav-library-link" },
  { to: "/documents", label: "nav.documents", icon: FolderOpen, tid: "mnav-documents-link" },
  { to: "/agenda", label: "nav.agenda", icon: CalendarDays, tid: "mnav-agenda-link" },
  { to: "/favoris", label: "nav.favorites", icon: Heart, tid: "mnav-favorites-link" },
];
const NAV_MOBILE_MORE = [
  { to: "/achats", label: "nav.purchases", icon: ShoppingBasket, tid: "mnav-purchases-link" },
  { to: "/setlists", label: "nav.setlists", icon: ListMusic, tid: "mnav-setlists-link" },
  { to: "/presence", label: "nav.presence", icon: ClipboardCheck, tid: "mnav-presence-link" },
  { to: "/inventaire", label: "nav.inventory", icon: Package, tid: "mnav-inventory-link" },
  { to: "/utilisateurs", label: "nav.users", icon: Users, tid: "mnav-users-link" },
  { to: "/parametres", label: "nav.settings", icon: Settings, tid: "mnav-settings-link" },
  { to: "/aide", label: "nav.help", icon: LifeBuoy, tid: "mnav-help-link" },
];

// Espace personnel : navigation réduite (bibliothèque, documents, favoris, paramètres)
const PERSONAL_MAIN = NAV_MAIN.filter((n) => ["/bibliotheque", "/documents", "/favoris"].includes(n.to));
const PERSONAL_BOTTOM = NAV_BOTTOM.filter((n) => n.to === "/parametres");
const PERSONAL_MOBILE = [
  { to: "/bibliotheque", label: "nav.library", icon: LibraryBig, tid: "mnav-library-link" },
  { to: "/documents", label: "nav.documents", icon: FolderOpen, tid: "mnav-documents-link" },
  { to: "/favoris", label: "nav.favorites", icon: Heart, tid: "mnav-favorites-link" },
  { to: "/parametres", label: "nav.settings", icon: Settings, tid: "mnav-settings-link" },
];
// Navigation Espace Enseignement (contexte teacher / student)
const TEACH_NAV_TEACHER = [
  { to: "/tableau-de-bord", label: "nav.dashboard", icon: LayoutDashboard, tid: "nav-dashboard-link" },
  { to: "/enseignement/eleves", label: "teach.nav_students", icon: GraduationCap, tid: "nav-students-link" },
  { to: "/enseignement/classes", label: "teach.nav_classes", icon: School, tid: "nav-classes-link" },
  { to: "/enseignement/agenda", label: "teach.nav_agenda", icon: CalendarDays, tid: "nav-agenda-link" },
  { to: "/enseignement/travail", label: "teach.nav_assignments", icon: ClipboardCheck, tid: "nav-assignments-link" },
  { to: "/enseignement/ressources", label: "teach.nav_resources", icon: FolderOpen, tid: "nav-resources-link" },
  { to: "/parametres", label: "nav.settings", icon: Settings, tid: "nav-settings-link" },
];
const TEACH_NAV_STUDENT = [
  { to: "/tableau-de-bord", label: "nav.dashboard", icon: LayoutDashboard, tid: "nav-dashboard-link" },
  { to: "/enseignement/cours", label: "teach.nav_lessons", icon: CalendarDays, tid: "nav-lessons-link" },
  { to: "/enseignement/classes", label: "teach.nav_my_classes", icon: School, tid: "nav-classes-link" },
  { to: "/enseignement/travail", label: "teach.nav_assignments", icon: ClipboardCheck, tid: "nav-assignments-link" },
  { to: "/enseignement/mes-ressources", label: "teach.nav_resources", icon: FolderOpen, tid: "nav-resources-link" },
  { to: "/parametres", label: "nav.settings", icon: Settings, tid: "nav-settings-link" },
];
const TEACH_MNAV_TEACHER = [
  { to: "/tableau-de-bord", label: "nav.home", icon: Home, tid: "mnav-home-link" },
  { to: "/enseignement/eleves", label: "teach.nav_students", icon: GraduationCap, tid: "mnav-students-link" },
  { to: "/enseignement/classes", label: "teach.nav_classes", icon: School, tid: "mnav-classes-link" },
  { to: "/enseignement/agenda", label: "teach.nav_agenda", icon: CalendarDays, tid: "mnav-agenda-link" },
  { to: "/enseignement/travail", label: "teach.nav_assignments", icon: ClipboardCheck, tid: "mnav-assignments-link" },
  { to: "/enseignement/ressources", label: "teach.nav_resources", icon: FolderOpen, tid: "mnav-resources-link" },
];
const TEACH_MNAV_STUDENT = [
  { to: "/tableau-de-bord", label: "nav.home", icon: Home, tid: "mnav-home-link" },
  { to: "/enseignement/cours", label: "teach.nav_lessons", icon: CalendarDays, tid: "mnav-lessons-link" },
  { to: "/enseignement/classes", label: "teach.nav_my_classes", icon: School, tid: "mnav-classes-link" },
  { to: "/enseignement/travail", label: "teach.nav_assignments", icon: ClipboardCheck, tid: "mnav-assignments-link" },
  { to: "/enseignement/mes-ressources", label: "teach.nav_resources", icon: FolderOpen, tid: "mnav-resources-link" },
];

function GroupLogo({ size = "h-10 w-10", onClick, testid }) {
  const { t } = useAuth();
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className={`${size} rounded-lg brand-bg flex items-center justify-center`} onClick={onClick} data-testid={testid}>
        <Music2 className="h-5 w-5 text-white" />
      </div>
    );
  }
  return (
    <img
      src={logoUrl()}
      alt={t("common.group_logo")}
      className={`${size} rounded-lg object-cover ${onClick ? "cursor-pointer" : ""}`}
      onError={() => setErr(true)}
      onClick={onClick}
      data-testid={testid}
    />
  );
}

// Menu de bascule multi-groupes (clic sur le logo) : groupe actif, autres comptes connectés,
// ajout d'un groupe (max 4) et déconnexion
function GroupSwitchMenu({ size = "h-10 w-10", testid }) {
  const { user, group, tspace, accounts, memberships, switchAccount, switchGroup, logout, t } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Groupes de la même identité RANNEA (bascule native sans re-saisie) ; les comptes
  // enregistrés sur l'appareil n'apparaissent que s'ils ne doublonnent pas une membership.
  // La console plateforme (superadmin) n'est pas un groupe : affichée dans une section dédiée en bas du menu
  const superEntry = memberships.find((m) => m.kind === "superadmin" && m.membership_id !== user?.id);
  const myGroups = memberships.filter((m) => m.membership_id !== user?.id && m.kind !== "superadmin");
  const others = accounts.filter((a) => a.login_id !== user?.login_id
    && !memberships.some((m) => (m.group_id || m.teacher_space_id || null) === (a.group_id || null)));

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="shrink-0 rounded-lg" data-testid={`${testid}-menu-trigger`} aria-label={t("layout.my_groups")}>
            <GroupLogo size={size} testid={testid} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2 card-bg border-app" align="start" data-testid="group-switch-menu">
          <p className="text-[10px] uppercase tracking-wider t-secondary px-2 pt-1 pb-2">{t("layout.my_groups")}</p>
          <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-white/5" data-testid="group-switch-current">
            <Check className="h-4 w-4 brand shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{group?.kind === "personal" ? t("layout.my_space") : (group?.name || tspace?.name)}</p>
              <p className="text-[10px] t-secondary truncate">{t("layout.current_group")} — {user?.login_id}</p>
            </div>
          </div>
          {myGroups.map((m) => (
            <button key={m.membership_id} onClick={() => switchGroup(m.membership_id)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors" data-testid={`group-switch-membership-${m.membership_id}`}>
              <Music2 className="h-4 w-4 t-secondary shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{m.group_kind === "personal" ? t("layout.my_space") : m.group_name}</p>
                <p className="text-[10px] t-secondary truncate">
                  {["admin", "music_leader", "musician", "member"].includes(m.role) ? t(`usr.role_${m.role}`) : (m.role || "")}
                  {m.instrument ? ` · ${m.instrument}` : ""}
                </p>
              </div>
            </button>
          ))}
          {others.length > 0 && (
            <p className="text-[10px] uppercase tracking-wider t-secondary px-2 pt-2 pb-1" data-testid="group-switch-other-accounts-label">{t("layout.other_accounts")}</p>
          )}
          {others.map((a) => (
            <button key={a.login_id} onClick={() => switchAccount(a)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors" data-testid={`group-switch-${a.login_id}`}>
              <Music2 className="h-4 w-4 t-secondary shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{a.group_name}</p>
                <p className="text-[10px] t-secondary truncate">{a.login_id}</p>
              </div>
            </button>
          ))}
          <button onClick={() => { setOpen(false); navigate("/espaces/nouveau"); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left text-sm brand transition-colors" data-testid="group-switch-create-space">
            <Plus className="h-4 w-4 shrink-0" /> {t("layout.create_space")}
          </button>
          {superEntry && (
            <>
              <p className="text-[10px] uppercase tracking-wider t-secondary px-2 pt-2 pb-1 mt-1 border-t border-app" data-testid="group-switch-platform-label">{t("layout.platform_section")}</p>
              <button onClick={() => switchGroup(superEntry.membership_id)} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left transition-colors" data-testid="group-switch-superadmin">
                <Shield className="h-4 w-4 brand shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{t("layout.platform_console")}</p>
                  <p className="text-[10px] t-secondary truncate">{t("layout.platform_console_desc")}</p>
                </div>
              </button>
            </>
          )}
          {tspace && (
            <button onClick={() => { setOpen(false); navigate("/parametres"); }} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left text-sm t-secondary transition-colors" data-testid="group-switch-settings">
              <Settings className="h-4 w-4 shrink-0" /> {t("nav.settings")}
            </button>
          )}
          <div className="border-t border-app mt-1 pt-1">
            <button onClick={logout} className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 text-left text-sm text-rose-400 transition-colors" data-testid="group-switch-logout">
              <LogOut className="h-4 w-4 shrink-0" /> {t("common.logout")}
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}

function NotificationBell() {
  const { t, lang } = useAuth();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    ensurePushSubscription();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const readAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-white/5 transition-colors"
          data-testid="notification-bell-button"
          aria-label={t("layout.notifications")}
        >
          <Bell className="h-6 w-6 t-secondary" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full accent-bg text-[10px] font-bold text-slate-900 flex items-center justify-center" data-testid="notification-unread-badge">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 card-bg border-app" align="end" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-app">
          <span className="font-heading font-bold text-sm">{t("layout.notifications")}</span>
          {data.unread > 0 && (
            <button className="text-xs brand hover:underline" onClick={readAll} data-testid="notification-read-all-button">
              {t("layout.mark_all_read")}
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.notifications.length === 0 && (
            <p className="text-sm t-secondary px-4 py-6 text-center">{t("layout.no_notifications")}</p>
          )}
          {data.notifications.map((n) => (
            <div key={n.id} className={`px-4 py-3 border-b border-app/50 text-sm ${n.read ? "opacity-60" : ""}`} data-testid={`notification-item-${n.id}`}>
              <p className="t-primary">{n.message}</p>
              <p className="text-xs t-secondary mt-1">
                {new Date(n.created_at).toLocaleString(dateLocale(lang), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AvailabilityReminder() {
  const { t, lang } = useAuth();
  const [next, setNext] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/events/next").then((r) => {
      if (r.data.event && !r.data.my_status) setNext(r.data.event);
    }).catch(() => {});
  }, []);

  if (!next || dismissed) return null;

  const answer = async (status) => {
    try {
      await api.post(`/events/${next.id}/availability`, { status });
      toast.success(t("common.availability_saved"));
    } catch (e) {
      toast.error(e.friendly);
    }
    setDismissed(true);
  };

  return (
    <Dialog open={true} onOpenChange={() => setDismissed(true)}>
      <DialogContent className="card-bg border-app" data-testid="availability-reminder-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("layout.reminder_title")}</DialogTitle>
          <DialogDescription className="t-secondary">
            {next.title} — {new Date(next.date + "T00:00").toLocaleDateString(dateLocale(lang), { weekday: "long", day: "numeric", month: "long" })}
            {next.start_time ? ` à ${next.start_time}` : ""}{next.location ? ` — ${next.location}` : ""}
          </DialogDescription>
        </DialogHeader>
        <AvailabilityButtons value={null} onChange={answer} testidPrefix="reminder-availability" />
      </DialogContent>
    </Dialog>
  );
}

// Rappel de cotisation à la connexion (max 1×/jour) : montant + RIB copiable + « J'ai payé » — activé par l'admin
function FeeReminder() {
  const { t, lang } = useAuth();
  const [fee, setFee] = useState(null);
  const [rib, setRib] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    api.get("/fees/my").then((r) => {
      const d = r.data;
      if (!d?.enabled || !d.alert_enabled) return;
      const current = (d.fees || []).find((f) => f.period_id === d.current_period_id);
      if (!current || current.status !== "due") return;
      if (localStorage.getItem(`rannea_fee_dismissed_${current.id}`) === new Date().toISOString().slice(0, 10)) return;
      setFee(current);
      setRib({ holder: d.rib_holder, iban: d.rib_iban, bic: d.rib_bic });
    }).catch(() => {});
  }, []);

  if (!fee || dismissed) return null;

  const later = () => {
    localStorage.setItem(`rannea_fee_dismissed_${fee.id}`, new Date().toISOString().slice(0, 10));
    setDismissed(true);
  };

  const claim = async () => {
    try {
      await claimFeePaid(fee.id);
      toast.success(t("myfees.claim_done"));
    } catch (e) {
      toast.error(e.friendly);
    }
    setDismissed(true);
  };

  return (
    <Dialog open={true} onOpenChange={later}>
      <DialogContent className="card-bg border-app" data-testid="fee-reminder-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><HandCoins className="h-5 w-5 accent" /> {t("myfees.banner_title")}</DialogTitle>
          <DialogDescription className="t-secondary">
            {fee.period_name} — <span className="font-semibold text-white">{eurCents(fee.amount_due_cents)}</span>
            {fee.period_due_date ? ` · ${t("myfees.due_date", { date: new Date(fee.period_due_date + "T00:00").toLocaleDateString(dateLocale(lang)) })}` : ""}
          </DialogDescription>
        </DialogHeader>
        <FeeRib rib={rib} />
        <div className="flex gap-2 mt-1">
          <Button className="brand-bg text-white flex-1" onClick={claim} data-testid="fee-reminder-claim">{t("myfees.claim_paid")}</Button>
          <Button variant="outline" className="border-app" onClick={later} data-testid="fee-reminder-later">{t("myfees.later")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout() {
  const { user, group, tspace, logout, t, refresh } = useAuth();
  const isTeaching = !!tspace;
  const isPersonal = group?.kind === "personal";
  // Espace personnel : pas de tableau de bord — redirection vers la bibliothèque
  useEffect(() => {
    if (group?.kind === "personal" && ["/", "/tableau-de-bord"].includes(window.location.pathname)) {
      navigate("/bibliotheque", { replace: true });
    }
  }, [group?.kind]);
  const teachNav = tspace?.my_role === "teacher" ? TEACH_NAV_TEACHER : TEACH_NAV_STUDENT;
  const teachMNav = tspace?.my_role === "teacher" ? TEACH_MNAV_TEACHER : TEACH_MNAV_STUDENT;
  const spaceName = group?.name || tspace?.name;
  // Tutoriel de découverte : affiché au premier lancement (flag serveur par utilisateur)
  const [showTuto, setShowTuto] = useState(false);
  useEffect(() => {
    if (user && user.display_prefs?.tutorial_seen !== true) setShowTuto(true);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeTutorial = () => {
    setShowTuto(false);
    api.put("/display/prefs", { tutorial_seen: true }).then(() => refresh()).catch(() => {});
  };
  // Rejouer le tutoriel depuis Paramètres ou le centre d'aide
  useEffect(() => {
    const h = () => setShowTuto(true);
    window.addEventListener("rannea:replay-tutorial", h);
    return () => window.removeEventListener("rannea:replay-tutorial", h);
  }, []);
  // Visite guidée interactive (déclenchée depuis le centre d'aide)
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    const h = () => setShowTour(true);
    window.addEventListener("rannea:start-tour", h);
    return () => window.removeEventListener("rannea:start-tour", h);
  }, []);
  // Réglage des notifications : demandé une seule fois, après le tutoriel (flag par utilisateur)
  const [showNotifSetup, setShowNotifSetup] = useState(false);
  useEffect(() => {
    if (user && user.display_prefs?.tutorial_seen === true && user.display_prefs?.notif_setup_seen !== true) setShowNotifSetup(true);
  }, [user?.display_prefs?.tutorial_seen, user?.display_prefs?.notif_setup_seen]); // eslint-disable-line react-hooks/exhaustive-deps
  const closeNotifSetup = () => {
    setShowNotifSetup(false);
    api.put("/display/prefs", { notif_setup_seen: true }).then(() => refresh()).catch(() => {});
  };
  const navigate = useNavigate();
  const player = usePlayer();
  const playerRef = useRef(player);
  playerRef.current = player;
  const [moreOpen, setMoreOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  const touchY = useRef(null);

  // Glisser vers la droite (mobile) : ferme/minimise le média en cours (audio, PDF, vidéo),
  // sinon revient à la page précédente. Listeners au niveau document car la page audio
  // plein écran est montée hors du Layout (PlayerProvider).
  useEffect(() => {
    let start = null;
    const onStart = (e) => {
      start = null;
      if (!window.matchMedia("(max-width: 1023px)").matches) return;
      if (e.target.closest('input[type="range"], video, audio, iframe, [role="slider"]')) return;
      if (isHorizontalScrollZone(e.target)) return; // zone scrollable horizontalement : le geste lui appartient
      start = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const onEnd = (e) => {
      if (!start) return;
      const dx = e.changedTouches[0].clientX - start.x;
      const dy = Math.abs(e.changedTouches[0].clientY - start.y);
      start = null;
      if (dx < 70 || dy > 60) return;
      if (e.__ranneaSwipeHandled) return; // une page a déjà géré le geste (navigation hiérarchique)
      // 1. Lecteur audio plein écran ouvert → minimise (la lecture continue en arrière-plan)
      if (playerRef.current?.isOpen) { playerRef.current.minimize(); return; }
      // 2. PDF / vidéo / autre modale ouverte → fermeture via Escape natif Radix
      if (document.querySelector('[data-testid="pdf-viewer-dialog"], [data-testid="media-player-dialog"]')) {
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
        return;
      }
      // 3. Sinon → page précédente
      navigate(-1);
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [navigate]);
  // Abonnement désactivé jusqu'à nouvel ordre : seul le blocage manuel superadmin s'applique
  const suspendedBlock = !!group?.suspended;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-base font-medium transition-colors ${
      isActive ? "brand-bg text-white" : "t-secondary hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="min-h-screen app-bg flex overflow-x-hidden" data-testid="app-layout">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex w-64 flex-col sidebar-bg border-r border-app fixed inset-y-0 left-0 z-30">
        <div className="flex items-center gap-3 px-5 pt-6 pb-4">
          <GroupSwitchMenu testid="sidebar-group-logo" />
          <div className="min-w-0">
            <p className="font-heading font-bold truncate">{spaceName}</p>
            <p className="text-xs t-secondary truncate">{t("layout.greeting", { name: user?.prenom })}</p>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {isTeaching ? (
            teachNav.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.tid}>
                <n.icon className="h-5 w-5" /> {t(n.label)}
              </NavLink>
            ))
          ) : (
            <>
              {(isPersonal ? PERSONAL_MAIN : NAV_MAIN).map((n) => (
                <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.tid}>
                  <n.icon className="h-5 w-5" /> {t(n.label)}
                </NavLink>
              ))}
              <div className="pt-4 mt-4 border-t border-app space-y-1">
                {(isPersonal ? PERSONAL_BOTTOM : NAV_BOTTOM).map((n) => (
                  <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.tid}>
                    <n.icon className="h-5 w-5" /> {t(n.label)}
                  </NavLink>
                ))}
              </div>
            </>
          )}
        </nav>
      </aside>

      {/* Header mobile : logo (→ menu multi-groupes) + nom du groupe + bonjour, cloche à droite */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 sidebar-bg border-b border-app px-4 py-2.5 flex items-center justify-between" data-testid="mobile-header">
        <div className="flex items-center gap-3 min-w-0">
          <GroupSwitchMenu size="h-10 w-10" testid="mobile-group-logo" />
          <div className="min-w-0">
            <p className="font-heading font-bold text-sm truncate" data-testid="mobile-group-name">{spaceName}</p>
            <p className="text-xs t-secondary truncate" data-testid="mobile-greeting">{t("layout.greeting", { name: user?.prenom })}</p>
          </div>
        </div>
        {!isTeaching && <NotificationBell />}
      </div>

      {/* Contenu */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen min-w-0">
        <header className="hidden md:flex items-center justify-between px-8 py-4 border-b border-app">
          <h1 className="font-heading text-2xl font-bold">{t("layout.greeting", { name: user?.prenom })}</h1>
          {!isTeaching && <NotificationBell />}
        </header>
        <main className="flex-1 flex flex-col px-4 md:px-8 pt-20 md:pt-6 pb-24 md:pb-8 min-w-0 w-full overflow-x-clip">
          {!online && (
            <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm flex items-center gap-2" data-testid="offline-banner">
              <WifiOff className="h-4 w-4 shrink-0" /> {t("offline.banner")}
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Bottom bar mobile (enseignement : onglets directs, sans panneau d'extension) */}
      {!isTeaching && !isPersonal && moreOpen && (
        <div className="md:hidden fixed inset-0 z-20 bg-black/40" onClick={() => setMoreOpen(false)} data-testid="mnav-more-scrim" />
      )}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30">
        {!isTeaching && !isPersonal && (
          <>
        <div
          className="flex justify-center sidebar-bg border-t border-app rounded-t-xl pt-1 cursor-pointer select-none"
          onClick={() => setMoreOpen((v) => !v)}
          onTouchStart={(e) => { touchY.current = e.touches[0].clientY; }}
          onTouchEnd={(e) => {
            const dy = e.changedTouches[0].clientY - (touchY.current ?? 0);
            if (dy < -25) setMoreOpen(true);
            else if (dy > 25) setMoreOpen(false);
          }}
          data-testid="mnav-more-toggle"
          aria-label={t("layout.more_tabs")}
        >
          <ChevronUp className={`h-4 w-4 t-secondary transition-transform duration-300 ${moreOpen ? "rotate-180" : ""}`} />
        </div>
        <div className={`sidebar-bg overflow-hidden transition-all duration-300 ${moreOpen ? "max-h-40" : "max-h-0"}`} data-testid="mnav-more-panel">
          <div className="grid grid-cols-5 px-2 pb-1">
            {NAV_MOBILE_MORE.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.tid}
                onClick={() => setMoreOpen(false)}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 px-1 py-2 text-[10px] leading-tight text-center ${isActive ? "brand" : "t-secondary"}`
                }
              >
                <n.icon className="h-5 w-5" />
                {t(n.label)}
              </NavLink>
            ))}
          </div>
        </div>
          </>
        )}
        <nav className="sidebar-bg grid py-2 border-t border-app" style={{ gridTemplateColumns: `repeat(${(isPersonal ? PERSONAL_MOBILE : isTeaching ? teachMNav : NAV_MOBILE).length}, minmax(0, 1fr))` }} data-testid="mobile-bottom-nav">
          {(isPersonal ? PERSONAL_MOBILE : isTeaching ? teachMNav : NAV_MOBILE).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-1 min-w-0 text-[10px] leading-tight text-center ${isActive ? "brand" : "t-secondary"}`
              }
            >
              <n.icon className="h-5 w-5 shrink-0" />
              <span className="truncate w-full">{t(n.label)}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {suspendedBlock && (
        <div className="fixed inset-0 z-[100] app-bg flex items-center justify-center p-6" data-testid="suspended-screen">
          <div className="app-card p-8 max-w-md w-full text-center space-y-4">
            <h1 className="font-heading text-2xl font-bold">{t("layout.suspended_title")}</h1>
            <p className="text-sm t-secondary">{t("layout.suspended_text")}</p>
            <button onClick={logout} className="text-xs t-secondary hover:text-rose-400" data-testid="suspended-logout-button">{t("common.logout")}</button>
          </div>
        </div>
      )}

      {!isTeaching && <AvailabilityReminder />}
      {!isTeaching && <FeeReminder />}
      {showTuto && !isTeaching && <TutorialCarousel onClose={closeTutorial} />}
      {showTour && <GuidedTour onClose={() => setShowTour(false)} />}
      {showNotifSetup && !showTuto && !isTeaching && <NotifSetupDialog onClose={closeNotifSetup} />}
    </div>
  );
}
